import Resource from 'Resources/ResourceTyped';
import type TicketModel from './Model/Ticket';
import type Client from './Model/Client';
import type Contact from './Model/Contact';
import { BuildFilters } from 'Resources/RequestBuilder';
import { SidebarCountFiltersBuilt, SidebarCountResult } from 'App/components/ListViewSidebar/Context/Helpers';

type TicketSave = PartialOmit<
	TicketModel,
	| 'status'
	| 'contact'
	| 'type'
	| 'client'
	| 'involved'
	| 'activity'
	| 'appointment'
	| 'opportunity'
	| 'relatedTicket'
	| 'agreement'
	| 'projectPlan'
> & {
	status?: PartialPick<TicketModel['status'], 'id'>;
	type?: PartialPick<TicketModel['type'], 'id'>;
	contact?: PartialPick<Contact, 'id'> | null;
	client?: PartialPick<Client, 'id'> | null;
	activity?: { id: number } | null;
	appointment?: { id: number } | null;
	opportunity?: { id: number } | null;
	relatedTicket?: { id: number } | null;
	agreement?: { id: number } | null;
	projectPlan?: { id: number } | null;
	involved?: { type: 'TO' | 'CC'; email: string; contact: PartialPick<Contact, 'id'> | null }[];
};

class Ticket extends Resource<TicketModel, TicketModel, TicketSave> {
	eventName = 'ticket';
	constructor() {
		super('tickets');

		this.notifications = {
			save: () => ({
				title: 'default.saved',
				body: 'saved.ticket',
				style: 'success',
				icon: 'check',
				type: 'body'
			}),
			saveError: () => ({
				title: 'default.error',
				body: 'saveError.ticket',
				style: 'error',
				icon: 'times',
				type: 'body'
			}),
			delete: () => ({
				title: 'default.deleted',
				body: 'deleted.ticket',
				style: 'success',
				icon: 'check',
				type: 'body'
			}),
			deleteError: () => ({
				title: 'default.error',
				body: 'deleteError.ticket',
				style: 'error',
				icon: 'times',
				type: 'body'
			})
		};
	}

	async save(
		data: TicketSave & { attachments?: any[] },
		opts: {
			params?: { skipEmail?: boolean; isImitatingContact?: boolean };
			skipNotification?: boolean;
			skipEvent?: boolean;
		} = {}
	): ReturnType<Resource<TicketModel>['save']> {
		const saveObj = { ...data };
		if (saveObj.contact) {
			saveObj.contact = { id: saveObj.contact.id, name: saveObj.contact.name };
		}
		if (saveObj.client) {
			saveObj.client = { id: saveObj.client.id, name: saveObj.client.name };
		}

		return super.save(saveObj, { ...opts, parseFiles: true, useNewMapDates: true, fileKeys: ['attachments'] });
	}

	async addComment(
		id: number,
		message: string,
		attachments: any[],
		type: string,
		statusId?: number,
		externalId?: number
	) {
		const formData = new FormData();
		formData.append('message', message);
		formData.append('type', type);

		if (externalId) {
			formData.append('externalId', externalId.toString());
		}

		if (statusId) {
			formData.append('statusId', statusId.toString());
		}

		for (const attachment of attachments) {
			if (attachment.blob) {
				formData.append('file', attachment.blob);
			} else {
				formData.append(
					'attachments',
					JSON.stringify({ filename: attachment.filename, value: attachment.value })
				);
			}
		}

		return this._postRequest(id + '/comment', formData, { methodName: 'addComment' }).then(({ data }) => {
			return data;
		});
	}

	mergeTickets(data: { from: TicketModel | TicketModel[]; to: TicketModel; name: string }) {
		return this._postRequest('mergeTickets', data, { methodName: 'mergeTickets' }).then(({ data }) => {
			return data;
		});
	}

	async findRecommendedMergeTickets(filters: BuildFilters) {
		return this._postRequest(
			'findRecommendedMergeTickets',
			{ filters },
			{
				methodName: 'findRecommendedMergeTickets'
			}
		).then(({ data }) => {
			return data;
		});
	}

	async forwardConversation(
		id: number,
		commentIds: number[],
		emailToForwardTo: string,
		attachments?: any[],
		contextMessage?: string
	) {
		return this._postRequest(
			'forwardConversation',
			{ id, commentIds, emailToForwardTo, attachments: attachments || [], contextMessage: contextMessage || '' },
			{ methodName: 'forwardConversation' }
		).then(data => {
			return data;
		});
	}

	async linkCommentsToExternalContact(ticketId: number, externalId: number, commentIds: number[]) {
		return this._postRequest(
			`${ticketId}/externalContact/${externalId}/linkComments`,
			{ commentIds },
			{ methodName: 'linkCommentsToExternalContact' }
		).then(({ data }) => data);
	}

	async getLinkedCommentIdsForExternalContact(ticketId: number, externalId: number): Promise<number[]> {
		return this._getRequest(`${ticketId}/externalContact/${externalId}/linkedCommentIds`, {
			methodName: 'getLinkedCommentIdsForExternalContact'
		}).then((res: { data: { data: number[] } }) => res.data?.data ?? []);
	}

	async getListViewCounts(filters: Array<SidebarCountFiltersBuilt>): Promise<Array<SidebarCountResult>> {
		return this._postRequest('listCounts', { filters }, { methodName: 'getListViewCounts' }).then(({ data }) => {
			return data;
		});
	}

	async linkUdo(id: number, objectId: number, typeId: number) {
		return this._postRequest(id + '/udo', { objectId, typeId }, { methodName: 'linkUdo' }).then(({ data }) => {
			return data;
		});
	}

	async unlinkUdo(id: number, objectId: number, typeId: number) {
		return this._deleteRequest(id + '/udo/' + objectId + '/' + typeId, { methodName: 'unlinkUdo' }).then(
			({ data }) => {
				return data;
			}
		);
	}
}

const resource = new Ticket();

export default resource;
